/**
 * Reporting.
 *
 * ## The screen is a picker, not a menu of reports
 *
 * The competing product advertises "300+ reports" as a list you scroll. That
 * number is a cross product — dimensions times measures times an optional
 * second dimension — and presenting it as a list means a trader hunts for the
 * one they want among three hundred names, most of which they will never open.
 *
 * Here it is two selects and a filter. The same three hundred combinations are
 * reachable, plus the ones nobody thought to name, and the trader composes the
 * question instead of recognising it.
 *
 * ## The filter is applied on the server, once
 *
 * Nothing on this screen filters a returned set. Two screens filtering the same
 * data their own way is how they come to disagree about the same month, and the
 * disagreement is invisible until a trader adds two numbers that should match.
 *
 * ## Small buckets are dropped, not shown small
 *
 * A one-trade row with a 100% win rate is the single most misleading thing a
 * report like this can print. The minimum is a control rather than a constant
 * so the trader can lower it deliberately and see that they did.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type {
  Dimension, DimensionsResponse, Measure, ReportResponse, ReportSpec,
  SavedReportDto, StatisticsResponse, TradeFilterDto, WinLossResponse,
} from '@aegis/api-client';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { AllStatistics } from '../components/AllStatistics.js';

const DIMENSION_LABELS: Record<Dimension, string> = {
  symbol: 'Instrument', direction: 'Direction', day: 'Trading day',
  weekday: 'Day of week', hour: 'Hour of day', strategy: 'Strategy',
  tag: 'Tag', outcome: 'Outcome', duration: 'Hold time', size: 'Position size',
  price: 'Price level',
};

const MEASURE_LABELS: Record<Measure, string> = {
  netPnl: 'Net P&L', tradeCount: 'Trade count', winRate: 'Win rate',
  expectancy: 'Expectancy', profitFactor: 'Profit factor',
  avgWin: 'Average win', avgLoss: 'Average loss', rMultiple: 'Average R',
};

/** Measures that are money, so the formatter does not print "$0.62" for a rate. */
const MONEY: ReadonlySet<Measure> = new Set<Measure>([
  'netPnl', 'expectancy', 'avgWin', 'avgLoss',
]);
const RATIO: ReadonlySet<Measure> = new Set<Measure>(['winRate']);

const money = (s: string): string => {
  const n = Number(s);
  return `${n < 0 ? '−' : n > 0 ? '+' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const pct = (s: string): string => `${(Number(s) * 100).toFixed(1)}%`;

function formatValue(measure: Measure, value: string | null): string {
  // Null is a real answer: profit factor with no losses, R with no risk basis
  // recorded. Printing 0 there would be a claim the data does not support.
  if (value === null) return '—';
  if (MONEY.has(measure)) return money(value);
  if (RATIO.has(measure)) return pct(value);
  if (measure === 'tradeCount') return String(Math.round(Number(value)));
  return Number(value).toFixed(2);
}

const BLANK_FILTER: TradeFilterDto = {};

const DEFAULT_SPEC: ReportSpec = {
  dimension: 'symbol', measure: 'netPnl', minTrades: 5, filter: BLANK_FILTER,
};

export function ReportsScreen(): JSX.Element {
  const [meta, setMeta] = useState<DimensionsResponse | null>(null);
  const [spec, setSpec] = useState<ReportSpec>(DEFAULT_SPEC);
  const [result, setResult] = useState<ReportResponse | null>(null);
  const [winLoss, setWinLoss] = useState<WinLossResponse | null>(null);
  const [saved, setSaved] = useState<SavedReportDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [stats, setStats] = useState<StatisticsResponse | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [d, s] = await Promise.all([api.reportDimensions(), api.savedReports()]);
        setMeta(d);
        setSaved(s.reports);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load the report catalogue.');
      }
    })();
  }, []);

  const run = useCallback(async (next: ReportSpec) => {
    setBusy(true);
    try {
      // Fetched with the report so the block below can never be showing a
      // different filter than the table above it. Two requests, one filter,
      // one await — the same reason the filter is applied server-side at all.
      const [r, wl, st] = await Promise.all([
        api.runReport(next),
        api.winsVsLosses(next.dimension, next.filter),
        api.statistics(next.filter),
      ]);
      setResult(r);
      setWinLoss(wl);
      setStats(st);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not run that report.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { void run(spec); }, [run, spec]);

  const patch = (over: Partial<ReportSpec>): void => setSpec((s) => ({ ...s, ...over }));
  const patchFilter = (over: Partial<TradeFilterDto>): void =>
    setSpec((s) => ({ ...s, filter: { ...s.filter, ...over } }));

  const filtered = result !== null && result.matched !== result.total;

  const save = async (): Promise<void> => {
    const name = saveName.trim();
    if (!name) return;
    try {
      const row = await api.saveReport(name, spec);
      setSaved((s) => [...s, row]);
      setSaveName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    }
  };

  const remove = async (id: string): Promise<void> => {
    await api.deleteReport(id);
    setSaved((s) => s.filter((r) => r.id !== id));
  };

  const rows = useMemo(() => result?.cells ?? [], [result]);

  // Net P&L and win rate are shown beside every measure as context. When the
  // chosen measure IS one of them, the context column is the measure column —
  // printing it twice reads as two figures that happen to agree.
  const showNet = spec.measure !== 'netPnl';
  const showWinRate = spec.measure !== 'winRate';

  if (!meta) {
    return error
      ? <div className="notice error">{error}</div>
      : <div className="dim">Loading…</div>;
  }

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Reports</h2>
        <span className="dim">
          {meta.combinations.toLocaleString('en-US')} combinations, composed rather than listed
        </span>
      </div>

      {/* ---- the question ---- */}
      <div className="report-controls">
        <label>
          <span>Group by</span>
          <select
            value={spec.dimension}
            onChange={(e) => patch({ dimension: e.target.value as Dimension })}
          >
            {meta.dimensions.map((d) => (
              <option key={d} value={d}>{DIMENSION_LABELS[d]}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Measure</span>
          <select
            value={spec.measure}
            onChange={(e) => patch({ measure: e.target.value as Measure })}
          >
            {meta.measures.map((m) => (
              <option key={m} value={m}>{MEASURE_LABELS[m]}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Then by</span>
          <select
            value={spec.drill ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setSpec((s) => {
                const { drill: _drop, ...rest } = s;
                return v ? { ...rest, drill: v as Dimension } : rest;
              });
            }}
          >
            <option value="">— nothing —</option>
            {meta.dimensions
              .filter((d) => d !== spec.dimension)
              .map((d) => <option key={d} value={d}>{DIMENSION_LABELS[d]}</option>)}
          </select>
        </label>

        <label>
          <span>Minimum trades</span>
          <input
            type="number" min={1} max={1000}
            value={spec.minTrades ?? 1}
            onChange={(e) => patch({ minTrades: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
      </div>

      {/* ---- the filter, applied server-side ---- */}
      <details className="report-filter" open={Object.keys(spec.filter).length > 0}>
        <summary>
          Filter
          {filtered && (
            <span className="badge badge-muted">
              {result!.matched} of {result!.total}
            </span>
          )}
        </summary>
        <div className="report-controls">
          <label>
            <span>From</span>
            <input
              type="date" value={spec.filter.from ?? ''}
              onChange={(e) => patchFilter({ from: e.target.value || undefined })}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date" value={spec.filter.to ?? ''}
              onChange={(e) => patchFilter({ to: e.target.value || undefined })}
            />
          </label>
          <label>
            <span>Outcome</span>
            <select
              value={spec.filter.outcome ?? ''}
              onChange={(e) => patchFilter({
                outcome: (e.target.value || undefined) as TradeFilterDto['outcome'],
              })}
            >
              <option value="">Everything</option>
              <option value="wins">Wins</option>
              <option value="losses">Losses</option>
              <option value="scratches">Scratches</option>
            </select>
          </label>
          <label>
            <span>Direction</span>
            <select
              value={spec.filter.directions?.[0] ?? ''}
              onChange={(e) => patchFilter({
                directions: e.target.value ? [e.target.value as 'long' | 'short'] : undefined,
              })}
            >
              <option value="">Both</option>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </label>
        </div>
        {Object.keys(spec.filter).length > 0 && (
          <button
            className="btn btn-quiet compact"
            onClick={() => setSpec((s) => ({ ...s, filter: BLANK_FILTER }))}
          >
            Clear filter
          </button>
        )}
      </details>

      {error && <div className="notice error">{error}</div>}

      {/* ---- the answer ---- */}
      {rows.length === 0 && !busy ? (
        <EmptyState icon="analytics" title="Nothing meets that description">
          {filtered
            ? 'The filter excluded every trade. Widen it, or clear it and start again.'
            : `No bucket has ${spec.minTrades ?? 1} trades or more. Lower the minimum to see `
              + 'smaller samples — knowing they are small is the point of the control.'}
        </EmptyState>
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th>{DIMENSION_LABELS[spec.dimension]}</th>
              {spec.drill && <th>{DIMENSION_LABELS[spec.drill]}</th>}
              <th className="num">Trades</th>
              <th className="num">{MEASURE_LABELS[spec.measure]}</th>
              {showNet && <th className="num">Net P&amp;L</th>}
              {showWinRate && <th className="num">Win rate</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={`${c.key}:${c.subKey ?? ''}`}>
                <td>{c.key}</td>
                {spec.drill && <td className="dim">{c.subKey ?? '—'}</td>}
                <td className="num mono">{c.tradeCount}</td>
                <td className={`num mono ${
                  MONEY.has(spec.measure) && c.value !== null
                    ? (Number(c.value) < 0 ? 'neg' : 'pos') : ''
                }`}>
                  {formatValue(spec.measure, c.value)}
                </td>
                {showNet && (
                  <td className={`num mono ${Number(c.netPnl) < 0 ? 'neg' : 'pos'}`}>
                    {money(c.netPnl)}
                  </td>
                )}
                {showWinRate && <td className="num mono">{pct(c.winRate)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ---- wins beside losses ----
          Worth its own table rather than a second column: "my winners run forty
          minutes and my losers run four hours" is a sentence a trader can act
          on, and no single-axis report says it. */}
      {winLoss && winLoss.rows.length > 0 && (
        <section className="winloss-report">
          <h3 id="winloss-h">Wins beside losses, by {DIMENSION_LABELS[spec.dimension].toLowerCase()}</h3>
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="winloss-h">
            <table className="report-table">
              <thead>
                <tr>
                  <th>{DIMENSION_LABELS[spec.dimension]}</th>
                  <th className="num">Wins</th>
                  <th className="num">Won</th>
                  <th className="num">Losses</th>
                  <th className="num">Lost</th>
                </tr>
              </thead>
              <tbody>
                {winLoss.rows.map((r) => (
                  <tr key={r.key}>
                    <td>{r.key}</td>
                    <td className="num mono">{r.wins}</td>
                    <td className="num mono pos">{money(r.winNet)}</td>
                    <td className="num mono">{r.losses}</td>
                    <td className="num mono neg">{money(r.lossNet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---- saved reports ---- */}
      <section className="saved-reports">
        <h3>Saved</h3>
        <div className="form-row">
          <input
            type="text" placeholder="Name this view" value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
          <button className="btn btn-quiet compact" onClick={() => void save()} disabled={!saveName.trim()}>
            Save current
          </button>
        </div>
        {saved.length === 0 ? (
          <p className="panel-empty">
            A saved view stores the question, not the answer — it re-runs against
            whatever the journal holds when you open it.
          </p>
        ) : (
          <ul className="saved-list">
            {saved.map((r) => (
              <li key={r.id}>
                <button className="btn btn-quiet compact" onClick={() => setSpec(r.spec)}>
                  {r.name}
                </button>
                <span className="dim">
                  {DIMENSION_LABELS[r.spec.dimension]} · {MEASURE_LABELS[r.spec.measure]}
                </span>
                <button className="btn btn-quiet compact" onClick={() => void remove(r.id)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Last, deliberately. The composed report is the question the trader
          came with; this is the reference they scroll to when it did not
          answer it. Putting it first would bury the thing they asked for. */}
      {stats && <AllStatistics data={stats} />}
    </div>
  );
}
